"use client";

import { api } from "@v1/backend/convex/_generated/api";
import { useQuery } from "convex/react";
import { type ReactElement, useEffect } from "react";
import { HelpArticleView } from "./help-article-view";
import { HelpNotFoundInline } from "./help-not-found-inline";

/** Client article load via Convex websocket — no Next RSC roundtrip. */
export function HelpArticleClient({
  org_slug,
  page_id,
  embedded = false,
}: {
  readonly org_slug: string;
  readonly page_id: string;
  readonly embedded?: boolean;
}): ReactElement {
  const result = useQuery(api.pages.public_help.getPublicHelpPage, {
    org_slug,
    page_id,
  });

  useEffect(() => {
    if (!result || result.state !== "ok") return;
    document.title = `${result.page.title} – ${result.organization_name}`;
  }, [result]);

  if (result === undefined) {
    // Same column as HelpArticleView, so the text does not jump when it loads.
    return (
      <div className="mx-auto w-full max-w-[936px]" aria-hidden>
        <div className="max-w-[680px] animate-pulse">
          <div className="mb-6 h-3 w-40 rounded bg-muted/70" />
          <div className="h-8 w-72 max-w-full rounded bg-muted/80" />
          <div className="mt-3 h-3 w-48 rounded bg-muted/60" />
          <div className="mt-8 space-y-3">
            <div className="h-3 w-full rounded bg-muted/55" />
            <div className="h-3 w-full rounded bg-muted/55" />
            <div className="h-3 w-64 max-w-full rounded bg-muted/55" />
          </div>
        </div>
      </div>
    );
  }

  if (result.state !== "ok") return <HelpNotFoundInline />;

  return (
    <HelpArticleView
      result={result}
      includeJsonLd={false}
      embedded={embedded}
    />
  );
}
